import type { ComponentPropsWithoutRef, Ref } from 'react';

import { ToolBadge } from '@/shared/ui/ToolBadge';

import type { SmartStartCardProps } from './SmartStartCard.types';
import {
  smartStartCardV4Variants,
  type SmartStartCardV4VariantProps,
} from './SmartStartCard.v4.variants';

export interface SmartStartCardV4Props
  extends
    Omit<ComponentPropsWithoutRef<'div'>, 'title' | 'onClick'>,
    Omit<SmartStartCardProps, 'disabled'>,
    SmartStartCardV4VariantProps {
  /** React 19 ref-as-prop: forwarded to the underlying `<div>`. */
  ref?: Ref<HTMLDivElement>;
}

export function SmartStartCardV4({
  title,
  agentId,
  tabId,
  onClick,
  disabled,
  className,
  ref,
  ...rest
}: SmartStartCardV4Props) {
  const activateOnEnterOrSpace = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  return (
    <div
      ref={ref}
      onClick={disabled ? undefined : onClick}
      onKeyDown={activateOnEnterOrSpace}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      data-slot="smart-start-card"
      data-disabled={disabled}
      className={smartStartCardV4Variants({ disabled, className })}
      {...rest}
    >
      <ToolBadge toolId={agentId} category={tabId} />
      <p className="flex-1 min-w-0 truncate text-base font-medium text-foreground">{title}</p>
    </div>
  );
}

SmartStartCardV4.displayName = 'SmartStartCardV4';
